import type { AppProps } from "next/app";
import { HeroUIProvider } from "@heroui/system";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { useRouter } from "next/router";
import "@/styles/globals.css";
import Layout from "@/layouts/layout";
import { OpenBaasProvider } from "@/openbaas";
import { client } from "@/client";
import { Button, Image, Spacer, ToastProvider } from "@heroui/react";
import { Icon } from "@iconify/react";
import Link from "next/link";
import { QueryClient } from "@tanstack/react-query";
import { CatalogProvider } from "@/context/with-provider";
import { AdminProvider } from "@/context/admin/admin-provider";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import { get, set, del } from "idb-keyval";
import { OrderProvider } from "@/backend/orders/infrastructura/context/OrderContext";

function createIDBPersister(idbKey = "reactQuery") {
  return {
    persistClient: async (client: any) => {
      await set(idbKey, client);
    },
    restoreClient: async () => {
      return await get(idbKey);
    },
    removeClient: async () => {
      await del(idbKey);
    },
  };
}

// Crear QueryClient una sola vez
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
    },
  },
});

// Crear persister
const idbPersister = createIDBPersister();
export async function handleLogout() {
  // 1. Limpia IndexedDB
  await idbPersister.removeClient();
  // 2. Limpia cache en memoria
  queryClient.clear();
}
function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: idbPersister,
      }}
    >
      <OpenBaasProvider
        uri={client.OPENBASS}
        Error401={
          <>
            <div className="flex min-h-screen w-full items-center justify-center bg-background p-4">
              <div className="flex w-full max-w-md flex-col items-center text-center">
                <div className="relative h-64 w-64 overflow-hidden rounded-xl">
                  <Image
                    removeWrapper
                    alt="404 Illustration"
                    className="h-full w-full object-cover"
                    src="https://img.heroui.chat/image/ai?w=512&h=512&u=404-error-page"
                  />
                </div>
                <Spacer y={6} />
                <h1 className="text-4xl font-bold text-foreground">404</h1>
                <Spacer y={2} />
                <h2 className="text-xl font-medium text-foreground">
                  Página no encontrada
                </h2>
                <Spacer y={4} />
                <p className="text-foreground-500">
                  Lo sentimos, la página que estás buscando no existe o ha sido
                  movida.
                </p>
                <Spacer y={6} />
                <Button
                  as={Link}
                  href="/"
                  color="primary"
                  variant="solid"
                  size="lg"
                  startContent={<Icon icon="lucide:home" width={18} />}
                  className="font-medium"
                >
                  Volver al inicio
                </Button>
              </div>
            </div>
          </>
        }
      >
        <HeroUIProvider navigate={router.push}>
          <ToastProvider maxVisibleToasts={5000} placement="top-right" />
          <NextThemesProvider defaultTheme="light">
            <AdminProvider>
              <OrderProvider>
                <CatalogProvider>
                  <Layout>
                    <Component {...pageProps} />
                  </Layout>
                </CatalogProvider>
              </OrderProvider>
            </AdminProvider>
          </NextThemesProvider>
        </HeroUIProvider>
      </OpenBaasProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
